import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Tag, Typography, Input, Select, Button, Space, Tooltip, Badge, message, type TagTone } from '../../ui';
import { Plus, Pencil, Ban, Phone, MessageCircle, Filter, Eraser, RotateCcw, CalendarClock } from 'lucide-react';
import { request } from '../../api/client';
import { usePermissionChecker } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { UserFormModal } from './UserFormModal';
import { DeactivateUserModal } from './DeactivateUserModal';
import { AttendanceUserDrawer, type AttUser } from '../ffa/AttendanceUserDrawer';
import { formatIst, formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';
import { HoverCascader, type CascOpt } from '../../components/HoverCascader';

const STATE_LABEL = STATE_NAME;
const ROLE_COLOR: Record<string, TagTone> = {
  SUPER_ADMIN: 'red', ADMIN: 'red', ACCOUNTS: 'gold',
  SALES_HEAD: 'purple', SALES_PERSON: 'blue', RETAILER: 'cyan', FARMER: 'green',
};
const STATUS_COLOR: Record<string, TagTone> = { active: 'green', suspended: 'red', locked: 'gold' };
const opt = (arr: any[]) => (arr ?? []).map((o: any) => ({ value: o.value, label: `${o.label}${o.count != null ? ` (${o.count})` : ''}` }));

// Staff cascader (Role → Team) labels/order, and territory-tree walkers for the hierarchical territory filter.
const STAFF_ROLE_LABELS: Record<string, string> = {
  SALES_HEAD: 'Sales Heads', SALES_PERSON: 'Sales Persons', ACCOUNTS: 'Accountants',
  WAREHOUSE_MANAGER: 'Warehouse Managers', ADMIN: 'Admins', SUPER_ADMIN: 'Super Admins',
};
const STAFF_ROLE_ORDER = ['SALES_HEAD', 'SALES_PERSON', 'ACCOUNTS', 'WAREHOUSE_MANAGER', 'ADMIN', 'SUPER_ADMIN'];
function findTreeNode(nodes: any[] | undefined, id: string): any {
  for (const n of nodes ?? []) { if (String(n.id) === String(id)) return n; const f = findTreeNode(n.children, id); if (f) return f; }
  return null;
}
function subtreeIds(node: any): string[] { return [String(node.id), ...(node.children ?? []).flatMap(subtreeIds)]; }
function ancestorIds(nodes: any[] | undefined, id: string, trail: string[] = []): string[] | null {
  for (const n of nodes ?? []) {
    if (String(n.id) === String(id)) return trail;
    const r = ancestorIds(n.children, id, [...trail, String(n.id)]);
    if (r) return r;
  }
  return null;
}

export function UsersPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('USER.CREATE');
  const canEdit = hasPerm('USER.EDIT');
  const canDelete = hasPerm('USER.DELETE');
  const canViewAttendance = hasPerm('ATTENDANCE.VIEW');
  const reactivate = useMutation({
    mutationFn: (id: string) => request({ url: `/users/${id}/reactivate`, method: 'PATCH' }),
    onSuccess: () => { message.success('User reactivated'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to reactivate'),
  });

  // ---- Filters (server-side) ----
  const [statusF, setStatusF] = useState<string[]>([]);
  const [langF, setLangF] = useState<string[]>([]);
  const [staffPath, setStaffPath] = useState<string[]>();   // Role → Team cascader
  const [terrPath, setTerrPath] = useState<string[]>();     // Territory cascader

  // Nested territory tree — drives the Territory cascader + its hierarchical (ancestors+descendants) expansion.
  const { data: terrTree } = useQuery({
    queryKey: ['territories-tree'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
  });

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    // Staff cascader → role (+ reportsTo when a manager's team is picked; 'none' = Sales Persons with no manager).
    const staffLeaf = staffPath?.length ? staffPath[staffPath.length - 1] : undefined;
    if (staffLeaf?.startsWith('role:')) p.role = staffLeaf.slice(5);
    else if (staffLeaf?.startsWith('mgr:')) { p.role = 'SALES_PERSON'; p.reportsTo = staffLeaf.slice(4); }
    // Territory cascader → coversTerritoryId = the picked node + its ancestors + descendants (by assignment).
    const terrLeaf = terrPath?.length ? terrPath[terrPath.length - 1] : undefined;
    if (terrLeaf) {
      const node = findTreeNode(terrTree, terrLeaf);
      const ids = [...new Set([...(ancestorIds(terrTree, terrLeaf) ?? []), ...(node ? subtreeIds(node) : [terrLeaf])])];
      if (ids.length) p.coversTerritoryId = ids.join(',');
    }
    if (statusF.length) p.status = statusF.join(',');
    if (langF.length) p.languagePref = langF.join(',');
    return p;
  }, [page, pageSize, search, staffPath, terrPath, statusF, langF, terrTree]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['users', params],
    queryFn: () => request<any>({ url: '/users', params }),
    placeholderData: keepPreviousData,
  });
  const { data: facets } = useQuery({
    queryKey: ['users', 'filter-options'],
    queryFn: () => request<any>({ url: '/users/filter-options' }),
  });

  // Staff cascader options: roles, with Sales Persons expanding into their managers' teams + a "No manager" bucket.
  const staffOptions = useMemo<CascOpt[]>(() => {
    const roleCodes = new Set((facets?.roles ?? []).map((r: any) => String(r.value)));
    const managers: CascOpt[] = (facets?.managers ?? []).map((m: any) => ({ value: `mgr:${m.value}`, label: `Under ${m.label}` }));
    return STAFF_ROLE_ORDER.filter((c) => roleCodes.has(c)).map((c) => ({
      value: `role:${c}`,
      label: STAFF_ROLE_LABELS[c] ?? c,
      children: c === 'SALES_PERSON' ? [...managers, { value: 'mgr:none', label: '— No manager —' }] : undefined,
    }));
  }, [facets]);
  // Territory cascader options: state → district → beat (the single India zone is collapsed; multi-zone shows zones).
  const territoryOptions = useMemo<CascOpt[]>(() => {
    const toOpt = (n: any): CascOpt => ({ value: String(n.id), label: n.name, children: n.children?.length ? n.children.map(toOpt) : undefined });
    const roots = terrTree ?? [];
    const start = (roots.length === 1 && roots[0].level === 'zone') ? (roots[0].children ?? []) : roots;
    return start.map(toOpt);
  }, [terrTree]);

  const terrName = useMemo(() => new Map<string, string>((facets?.territories ?? []).map((t: any) => [String(t.value), String(t.label)] as [string, string])), [facets]);

  // Resolve each territory → its own name + the state it belongs to, so the Location
  // column reads consistently as "Area · State" (or just the state for a state node).
  const { data: territories } = useQuery({
    queryKey: ['territories-for-users'],
    queryFn: () => request<any[]>({ url: '/territories' }),
  });
  type TerrInfo = { name: string; stateCode: string | null; level: string };
  const terrInfo = useMemo(
    () => new Map<string, TerrInfo>(
      (territories ?? []).map((t: any) => [String(t.id), { name: t.name, stateCode: t.stateCode, level: t.level }] as [string, TerrInfo]),
    ),
    [territories],
  );
  const territoryLabel = (id: any): string => {
    const t = terrInfo.get(String(id));
    if (!t) return terrName.get(String(id)) ?? String(id);
    const state = t.stateCode ? (STATE_LABEL.get(t.stateCode) ?? t.stateCode) : null;
    if (!state || t.level === 'state' || t.name === state) return state ?? t.name;
    return `${t.name} · ${state}`;
  };

  const [deactivating, setDeactivating] = useState<any | null>(null);
  const [attUser, setAttUser] = useState<AttUser | null>(null);

  const activeFilters = (staffPath?.length ? 1 : 0) + (terrPath?.length ? 1 : 0) + statusF.length + langF.length + (search ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStaffPath(undefined); setTerrPath(undefined); setStatusF([]); setLangF([]); setPage(1);
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'fullName', title: 'Name', dataIndex: 'fullName', sortable: true, alwaysVisible: true, width: 220, ellipsis: true },
    { key: 'mobile', title: 'Mobile', dataIndex: 'mobile', sortable: true, alwaysVisible: true, width: 150, render: (v: string) => v && <code>{v}</code> },
    { key: 'email', title: 'Email', dataIndex: 'email', sortable: true, width: 220, ellipsis: true,
      render: (v: string) => v && <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()}>{v}</a> },
    { key: 'role', title: 'Role', dataIndex: ['role', 'code'], width: 190, sortable: true,
      // One role per user (single-role policy) — show the single role badge.
      render: (v: string) => v && <Tag tone={ROLE_COLOR[v] ?? 'neutral'} style={{ margin: 0 }}>{v}</Tag> },
    { key: 'status', title: 'Status', dataIndex: 'status', width: 110, sortable: true,
      render: (v: string) => <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag> },
    { key: 'languagePref', title: 'Lang', dataIndex: 'languagePref', width: 90, defaultVisible: false,
      render: (v: string) => v && <Tag>{v.toUpperCase()}</Tag> },
    { key: 'location', title: 'Location', dataIndex: 'territoryId', width: 200, ellipsis: true, alwaysVisible: true,
      render: (v: any) => (v ? territoryLabel(v) : <span style={{ color: '#bbb' }}>—</span>) },
    { key: 'lastLoginAt', title: 'Last Login', dataIndex: 'lastLoginAt', width: 160, sortable: true,
      render: (v: string) => (v ? formatIst(v) : <Tag>Never</Tag>) },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', width: 120, sortable: true, defaultVisible: false, render: (v: string) => formatIstDate(v) },
    {
      key: 'actions', title: 'Actions', alwaysVisible: true, width: 180, fixed: 'right',
      render: (_: any, r: any) => {
        const showAtt = canViewAttendance && (r.role?.code === 'SALES_PERSON' || r.role?.code === 'SALES_HEAD');
        return (
          // Right-aligned, fixed-slot action cluster: the conditional Attendance button keeps a
          // placeholder slot when hidden, so every button lines up column-for-column across rows.
          <div className="flex w-full items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            {canEdit && (<Tooltip label="Edit"><Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => { setEditing(r); setFormOpen(true); }} /></Tooltip>)}
            <Tooltip label="Call"><Button variant="text" size="sm" aria-label="Call" icon={<Phone size={16} />} onClick={() => { window.location.href = `tel:${r.mobile}`; }} /></Tooltip>
            <Tooltip label="WhatsApp">
              <Button variant="text" size="sm" aria-label="WhatsApp" icon={<MessageCircle size={16} />} onClick={() => window.open(`https://wa.me/${(r.mobile ?? '').replace('+', '')}`, '_blank', 'noopener')} />
            </Tooltip>
            {showAtt ? (
              <Tooltip label="Attendance history">
                <Button variant="text" size="sm" aria-label="Attendance history" icon={<CalendarClock size={16} />} onClick={() => setAttUser({ id: String(r.id), name: r.fullName, role: r.role?.name ?? r.role?.code })} />
              </Tooltip>
            ) : (
              <span className="inline-block size-7 shrink-0" aria-hidden />
            )}
            {r.status === 'active' && canDelete && (
              <Tooltip label="Deactivate">
                <Button size="sm" variant="text" aria-label="Deactivate" icon={<Ban size={16} className="text-danger-600 dark:text-danger-500" />} onClick={() => setDeactivating(r)} />
              </Tooltip>
            )}
            {r.status !== 'active' && canDelete && (
              <Tooltip label="Reactivate — let this user log in again">
                <Button variant="text" size="sm" aria-label="Reactivate" icon={<RotateCcw size={16} />} style={{ color: '#1F8A4C', borderColor: '#1F8A4C' }}
                  loading={reactivate.isPending && reactivate.variables === r.id} onClick={() => reactivate.mutate(r.id)} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Users</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setFormOpen(true); }}>Add user</Button>
          )}
        </div>
      </div>
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input placeholder="Search name, email, mobile…" prefix={<Filter size={16} style={{ color: '#aaa' }} />}
            className="w-60" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} />
          <HoverCascader options={staffOptions} value={staffPath} onChange={(v) => { setStaffPath(v); setPage(1); }} placeholder="Role / Team" minWidth={200} />
          <HoverCascader options={territoryOptions} value={terrPath} onChange={(v) => { setTerrPath(v); setPage(1); }} placeholder="Territory" minWidth={180} />
          <Select mode="multiple" allowClear placeholder="Status" style={{ minWidth: 140 }} maxTagCount={1}
            value={statusF} onChange={(v) => { setStatusF(v); setPage(1); }} options={opt(facets?.statuses)} />
          <Select mode="multiple" allowClear placeholder="Language" style={{ minWidth: 130 }} maxTagCount={1}
            value={langF} onChange={(v) => { setLangF(v); setPage(1); }} options={opt(facets?.languages)} />
          {activeFilters > 0 && (
            <Badge count={activeFilters} size="small"><Button icon={<Eraser size={16} />} onClick={clearFilters}>Clear</Button></Badge>
          )}
        </div>

        <DataTable
          storageKey="users-list"
          rowKey="id"
          loading={isLoading || isFetching}
          dataSource={data?.data ?? []}
          columns={columns}
          scroll={{ x: 1300 }}
          onRow={(r) => ({
            onClick: canEdit ? () => { setEditing(r); setFormOpen(true); } : undefined,
            style: { cursor: canEdit ? 'pointer' : 'default' },
          })}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} users`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <UserFormModal open={formOpen} record={editing} onClose={() => { setFormOpen(false); setEditing(null); }} />
      <DeactivateUserModal open={!!deactivating} user={deactivating} onClose={() => setDeactivating(null)} />
      <AttendanceUserDrawer user={attUser} onClose={() => setAttUser(null)} />
    </div>
  );
}
